import { type LocalDate, addDays, isWeekend, parseLocalDate } from "@despejo/domain";
import type { BusinessDayAdjustment, Holiday } from "./types.js";

export class HolidayCalendarMissing extends Error {
  constructor(readonly year: number) {
    super(`Holiday calendar for ${year} is not loaded`);
  }
}

/**
 * Costa Rica business-day calendar. A year with no loaded holidays is an
 * error, never "no holidays": the plan requires HOLIDAY_CALENDAR_MISSING
 * instead of silently weekend-only logic (§35).
 */
export class BusinessCalendar {
  private readonly closed = new Set<string>();
  private readonly years: Set<number>;

  constructor(holidays: readonly Holiday[], loadedYears?: readonly number[]) {
    for (const h of holidays) if (h.business_closed) this.closed.add(h.date);
    this.years = new Set(loadedYears ?? holidays.map((h) => parseLocalDate(h.date).y));
  }

  hasYear(year: number): boolean {
    return this.years.has(year);
  }

  isBusinessDay(date: LocalDate): boolean {
    const { y } = parseLocalDate(date);
    if (!this.years.has(y)) throw new HolidayCalendarMissing(y);
    return !isWeekend(date) && !this.closed.has(date);
  }

  adjust(date: LocalDate, mode: BusinessDayAdjustment = "NEXT_BUSINESS_DAY"): LocalDate {
    if (mode === "NONE") return date;
    const step = mode === "NEXT_BUSINESS_DAY" ? 1 : -1;
    let d = date;
    for (let i = 0; i < 31; i++) {
      if (this.isBusinessDay(d)) return d;
      d = addDays(d, step);
    }
    throw new Error(`No business day within 31 days of ${date}`);
  }

  /** The Nth business day on or after `from` (n = 1 is the first business day >= from). */
  nthBusinessDay(from: LocalDate, n: number): LocalDate {
    if (n < 1) throw new RangeError("n must be >= 1");
    let d = from;
    let count = 0;
    for (let i = 0; i < 400; i++) {
      if (this.isBusinessDay(d)) {
        count++;
        if (count === n) return d;
      }
      d = addDays(d, 1);
    }
    throw new Error("business-day search overflow");
  }

  /** N business days strictly after `date` (acceptance windows). */
  addBusinessDays(date: LocalDate, n: number): LocalDate {
    return this.nthBusinessDay(addDays(date, 1), n);
  }
}
